import { useNavigate } from '@tanstack/react-router';
import {
  BotIcon,
  ChevronDownIcon,
  DownloadIcon,
  FileWarningIcon,
  Loader2Icon,
  RefreshCcwIcon,
  ShieldAlertIcon,
  SparklesIcon,
  TicketIcon,
} from 'lucide-react';
import { useRef, useState, type JSX, type ReactNode } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { Button } from '@/components/ui/button.js';
import {
  createBulkOutcomeReassessment,
  fetchRunApiLog,
  fetchRunResultsExport,
  QualityApiError,
  qualityKeys,
} from '@/lib/api/quality.js';
import type { ReportBundle, RunDetail } from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';

import {
  nextStepModel,
  selectActionCases,
  type NextStepId,
} from './nextSteps.js';
import { openInAgent } from './openInAgent.js';
import { sendRunAction } from './sendRunAction.js';
import type { PanelSend } from '@/features/assistant/usePanelSend.js';
import { RunNextSteps } from './RunNextSteps.js';
import { correctionRequestId } from '../OutcomeCorrectionDialog.js';
import {
  isOutcomeReassessmentCandidate,
  outcomeProjection,
  runLifecycleStatus,
} from '../quality-outcome.js';
import type { AgentAction } from './agentActionHandoff.js';
import {
  QualityRunActionsMenu,
  type QualityRunMenuAction,
} from '../QualityRunActionsMenu.js';

type ActionKind = NextStepId | 'api_log' | 'csv';

function reasonOf(err: unknown, fallback: string): string {
  if (err instanceof QualityApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// `@react-pdf/renderer` is large; load it only when the user actually downloads a report so it
// stays out of the run-detail route's initial bundle.
async function downloadReport(
  detail: RunDetail,
  bundle?: ReportBundle,
): Promise<void> {
  const { downloadRunReportPdf } = await import('./runReportPdf.js');
  await downloadRunReportPdf(detail, bundle);
}

/** Whether any step in the report made an API call, which is when the API log has content. */
function reportHasApiCalls(bundle?: ReportBundle): boolean {
  return (bundle?.cases?.cases ?? []).some((testCase) =>
    (testCase.steps ?? []).some(
      (step) => Array.isArray(step.api_calls) && step.api_calls.length > 0,
    ),
  );
}

/** Saves the run's API log (MEDA-914), which the service builds on request. */
async function downloadApiLog(runId: string): Promise<void> {
  const blob = await fetchRunApiLog(runId);
  const href = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = `api-log-${runId}.txt`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

/**
 * Keeps action controllers and their idempotency key mounted across menu, assistant and tab changes.
 * The shared-journal host combines its lifecycle actions and these handlers into one compact menu.
 * Blocked clicks explain the missing report or pending action through `notify`.
 */
export function RunActions({
  detail,
  bundle,
  reportUnavailable = false,
  open = true,
  nextSteps = false,
  onOpenChange,
  panelSend,
  pageActions = [],
  reportActions = true,
  menuVisible = true,
}: {
  panelSend?: PanelSend;
  detail: RunDetail;
  bundle?: ReportBundle;
  reportUnavailable?: boolean;
  open?: boolean;
  nextSteps?: boolean;
  onOpenChange?: (open: boolean) => void;
  pageActions?: QualityRunMenuAction[];
  reportActions?: boolean;
  menuVisible?: boolean;
}): JSX.Element {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<ActionKind | null>(null);
  const actionActive = useRef(false);
  const [prepareOpen, setPrepareOpen] = useState(false);
  /** One idempotency key per mounted run detail: a retried click re-sends the same batch instead of
   *  queueing a second analysis for every case. */
  const [batchId] = useState(correctionRequestId);
  const projection = outcomeProjection(detail.run, detail.summary);
  const reportChanged =
    bundle?.coherence === 'changed' ||
    (projection.kind === 'canonical' &&
      (bundle?.coherence !== 'coherent' ||
        bundle.reportRevision !== projection.reportRevision));
  const reportBlocked = reportUnavailable || reportChanged;
  const hasApiCalls = reportHasApiCalls(bundle);
  const businessExecution =
    (detail.run.run_kind ?? 'execution') === 'execution';

  const prepare = (action: AgentAction): Promise<void> => {
    if (projection.kind !== 'canonical')
      return Promise.reject(
        new Error('Canonical outcome data is unavailable.'),
      );
    const selected = selectActionCases(detail.results, action);
    if (selected.length === 0)
      return Promise.reject(new Error('No current cases match this action.'));
    const selection = {
      caseIds: selected.map((entry) => entry.caseId),
      outcomes: [...new Set(selected.map((entry) => entry.outcome))],
      reportRevision: projection.reportRevision,
    };
    if (panelSend)
      return sendRunAction(detail.run.id, action, panelSend, selection);
    return openInAgent({
      detail,
      bundle,
      mode: action,
      selection: {
        caseIds: selected.map((entry) => entry.caseId),
        outcomes: [...new Set(selected.map((entry) => entry.outcome))],
        reportRevision: projection.reportRevision,
      },
      navigate,
    });
  };

  /** Cases the server would plausibly accept. The server decides for real — this only keeps the
   *  button off a run where nothing could possibly be analyzed. */
  const reassessable = detail.results.filter(isOutcomeReassessmentCandidate);

  const reassessAll = async (): Promise<void> => {
    const result = await createBulkOutcomeReassessment(detail.run.id, {
      batchId,
    });
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: qualityKeys.run(detail.run.id),
      }),
      queryClient.invalidateQueries({
        queryKey: qualityKeys.reportBundleBase(detail.run.id),
      }),
      /** Without this every row menu keeps offering "Analyze with AI" until a reload: each case's
       *  eligibility query only polls once it already knows an analysis is active. */
      queryClient.invalidateQueries({
        queryKey: qualityKeys.outcomeReassessmentsBase(detail.run.id),
      }),
    ]);
    notify.success(
      result.started.length === 0
        ? 'No eligible cases were available for AI reassessment.'
        : `Started AI reassessment for ${result.started.length} eligible case${
            result.started.length === 1 ? '' : 's'
          }${
            result.skipped.length > 0
              ? `, skipped ${result.skipped.length}`
              : ''
          }.`,
    );
  };

  /** Resolves true only when the action succeeded, so the strip can tell a failure from a success. */
  const run = async (
    kind: ActionKind,
    fn: () => Promise<void>,
  ): Promise<boolean> => {
    if (actionActive.current) {
      notify.warning('Wait for the current action to finish.');
      return false;
    }
    if (reportBlocked && kind !== 'csv' && kind !== 'api_log') {
      notify.warning(
        'Wait for the current report revision to load, then try again.',
      );
      return false;
    }
    actionActive.current = true;
    setBusy(kind);
    try {
      await fn();
      return true;
    } catch (err) {
      notify.error(reasonOf(err, 'Action failed'));
      return false;
    } finally {
      actionActive.current = false;
      setBusy(null);
    }
  };

  const downloadResults = async (): Promise<void> => {
    const status = runLifecycleStatus(detail.run);
    if (
      ![
        'completed',
        'partially_completed',
        'not_completed',
        'passed',
        'failed',
        'incomplete',
        'cancelled',
      ].includes(status ?? '')
    )
      throw new Error(
        'Wait for this execution run to finish before exporting results.',
      );
    if (reportBlocked || projection.kind === 'unsupported')
      throw new Error(
        'Wait for the current report revision to load, then try exporting again.',
      );
    const blob = await fetchRunResultsExport(detail.run.id);
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = `execution-${detail.run.id}-results.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(href), 0);
  };

  const analyze = (): Promise<void> =>
    panelSend
      ? sendRunAction(detail.run.id, 'analyze', panelSend)
      : openInAgent({ detail, bundle, mode: 'analyze', navigate });
  const createTask = (): Promise<void> =>
    panelSend
      ? sendRunAction(detail.run.id, 'create-task', panelSend)
      : openInAgent({ detail, bundle, mode: 'create-task', navigate });
  const stepHandlers: Record<NextStepId, () => Promise<void>> = {
    file_defects: () => prepare('file_defects'),
    rework_cases: () => prepare('rework_cases'),
    report_qa_errors: () => prepare('report_qa_errors'),
    analyze,
    reassess_all: reassessAll,
    pdf: () => downloadReport(detail, bundle),
    task: createTask,
  };

  if (panelSend) {
    const actions = [...pageActions];
    if (reportActions) {
      if (projection.kind === 'canonical' && businessExecution) {
        if (reassessable.length > 0)
          actions.push({
            id: 'reassess_all',
            label: 'Reassess eligible cases',
            icon: <SparklesIcon aria-hidden />,
            onSelect: () => void run('reassess_all', reassessAll),
          });
        for (const action of [
          {
            id: 'file_defects',
            label: 'File defects',
            icon: <FileWarningIcon aria-hidden />,
          },
          {
            id: 'rework_cases',
            label: 'Rework cases',
            icon: <RefreshCcwIcon aria-hidden />,
          },
          {
            id: 'report_qa_errors',
            label: 'Report QA errors',
            icon: <ShieldAlertIcon aria-hidden />,
          },
        ] as const) {
          if (selectActionCases(detail.results, action.id).length > 0)
            actions.push({
              ...action,
              onSelect: () => void run(action.id, () => prepare(action.id)),
            });
        }
      }
      actions.push({
        id: 'pdf',
        label: 'Download report',
        icon: <DownloadIcon aria-hidden />,
        onSelect: () => void run('pdf', stepHandlers.pdf),
      });
      if (businessExecution)
        actions.push({
          id: 'csv',
          label: 'Download CSV',
          icon: <DownloadIcon aria-hidden />,
          onSelect: () => void run('csv', downloadResults),
        });
      if (hasApiCalls)
        actions.push({
          id: 'api_log',
          label: 'Download API log',
          icon: <DownloadIcon aria-hidden />,
          onSelect: () =>
            void run('api_log', () => downloadApiLog(detail.run.id)),
        });
      if (
        projection.kind !== 'unsupported' &&
        (projection.kind === 'legacy' || !businessExecution)
      )
        actions.push({
          id: 'task',
          label: 'Create task',
          icon: <TicketIcon aria-hidden />,
          onSelect: () => void run('task', createTask),
        });
    }
    return (
      <QualityRunActionsMenu actions={actions} menuVisible={menuVisible} />
    );
  }

  const strip =
    nextSteps && onOpenChange ? (
      <RunNextSteps
        key={detail.run.id}
        runId={detail.run.id}
        model={nextStepModel(detail)}
        busy={busy}
        onStep={(step) => run(step, stepHandlers[step])}
        actionsOpen={open}
        onActionsOpenChange={onOpenChange}
      />
    ) : null;

  const panel = (
    <section
      id="quality-run-actions-panel"
      role="region"
      aria-labelledby="quality-run-actions-title"
      hidden={!open}
      className={
        panelSend
          ? 'w-full'
          : 'w-full rounded-xl border border-border/70 bg-muted/10 p-3'
      }
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2
            id="quality-run-actions-title"
            className="text-xs font-medium text-muted-foreground"
          >
            Run actions
          </h2>
        </div>
        {reportBlocked ? (
          <p className="text-xs text-warning-700">
            {reportChanged
              ? 'Report changed. Export is available after the current revision loads.'
              : 'Report unavailable. Actions are available after the current revision loads.'}
          </p>
        ) : null}
      </div>

      <div
        className={
          panelSend
            ? 'flex flex-col gap-2 [&_[data-action-card]]:border-0 [&_[data-action-card]]:bg-transparent [&_[data-action-card]]:p-0 [&_[data-action-description]]:hidden'
            : 'grid grid-cols-1 gap-3 sm:grid-cols-2'
        }
      >
        <ActionCard
          category="Investigate"
          title="Agent investigation"
          description="Open a chat investigation with the run's current evidence."
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => void run('analyze', analyze)}
          >
            {busy === 'analyze' ? (
              <Loader2Icon className="animate-spin" aria-hidden />
            ) : (
              <BotIcon aria-hidden />
            )}
            Analyze run in Agent
          </Button>
        </ActionCard>

        {projection.kind === 'canonical' && businessExecution ? (
          <ActionCard
            category="AI outcome review"
            title="Outcome reassessment"
            description="Ask AI to reassess cases the server marks eligible; outcomes may update."
          >
            {reassessable.length > 0 ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void run('reassess_all', reassessAll)}
              >
                {busy === 'reassess_all' ? (
                  <Loader2Icon className="animate-spin" aria-hidden />
                ) : (
                  <SparklesIcon aria-hidden />
                )}
                Reassess eligible cases
              </Button>
            ) : (
              <p className="text-xs text-muted-foreground">
                No cases are currently eligible for reassessment.
              </p>
            )}
          </ActionCard>
        ) : null}

        {projection.kind === 'canonical' && businessExecution ? (
          <ActionCard
            category="Follow-up"
            title="Follow-up drafts"
            description="Open editable Agent drafts for selected defects, unclear cases, or QA errors."
          >
            <Button
              variant="outline"
              size="sm"
              aria-expanded={prepareOpen}
              aria-controls="quality-run-prepare-actions"
              onClick={() => setPrepareOpen((current) => !current)}
            >
              <ChevronDownIcon
                className={prepareOpen ? 'rotate-180' : undefined}
                aria-hidden
              />
              Prepare actions
            </Button>
            <div
              id="quality-run-prepare-actions"
              hidden={!prepareOpen}
              className={
                prepareOpen ? 'mt-2 flex flex-col items-start gap-2' : undefined
              }
            >
              <ActionButton
                action="file_defects"
                label="File defects"
                icon={<FileWarningIcon aria-hidden />}
                busy={busy}
                run={run}
                onRun={prepare}
              />
              <ActionButton
                action="rework_cases"
                label="Rework cases"
                icon={<RefreshCcwIcon aria-hidden />}
                busy={busy}
                run={run}
                onRun={prepare}
              />
              <ActionButton
                action="report_qa_errors"
                label="Report QA errors"
                icon={<ShieldAlertIcon aria-hidden />}
                busy={busy}
                run={run}
                onRun={prepare}
              />
            </div>
          </ActionCard>
        ) : null}

        <ActionCard
          category="Report"
          title="Export"
          description={
            hasApiCalls
              ? 'Download the report, case-aligned results, or API call log.'
              : businessExecution
                ? 'Download the report or case-aligned results.'
                : 'Export the current run report as a PDF.'
          }
        >
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => void run('pdf', stepHandlers.pdf)}
            >
              {busy === 'pdf' ? (
                <Loader2Icon className="animate-spin" aria-hidden />
              ) : (
                <DownloadIcon aria-hidden />
              )}
              Download report
            </Button>
            {businessExecution ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => void run('csv', downloadResults)}
              >
                {busy === 'csv' ? (
                  <Loader2Icon className="animate-spin" aria-hidden />
                ) : (
                  <DownloadIcon aria-hidden />
                )}
                Download CSV
              </Button>
            ) : null}
            {hasApiCalls ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  void run('api_log', () => downloadApiLog(detail.run.id))
                }
              >
                {busy === 'api_log' ? (
                  <Loader2Icon className="animate-spin" aria-hidden />
                ) : (
                  <DownloadIcon aria-hidden />
                )}
                Download API log
              </Button>
            ) : null}
          </div>
        </ActionCard>

        {projection.kind !== 'unsupported' &&
        (projection.kind === 'legacy' || !businessExecution) ? (
          <ActionCard
            category="Agent handoff"
            title="Task draft"
            description="Open an editable Agent draft for follow-up work."
          >
            <Button
              variant="outline"
              size="sm"
              onClick={() => void run('task', createTask)}
            >
              {busy === 'task' ? (
                <Loader2Icon className="animate-spin" aria-hidden />
              ) : (
                <TicketIcon aria-hidden />
              )}
              Create task
            </Button>
          </ActionCard>
        ) : null}
      </div>
    </section>
  );

  return (
    <>
      {strip}
      {panel}
    </>
  );
}

function ActionCard({
  category,
  title,
  description,
  children,
}: {
  category: string;
  title: string;
  description: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      data-action-card
      className="flex min-w-0 flex-col items-start gap-2 rounded-lg border border-border/70 bg-background p-3"
    >
      <div data-action-description>
        <p className="text-[0.68rem] font-medium uppercase tracking-wide text-muted-foreground">
          {category}
        </p>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
          {description}
        </p>
      </div>
      {children}
    </div>
  );
}

function ActionButton({
  action,
  label,
  icon,
  busy,
  run,
  onRun,
}: {
  action: AgentAction;
  label: string;
  icon: JSX.Element;
  busy: ActionKind | null;
  run: (kind: ActionKind, fn: () => Promise<void>) => Promise<boolean>;
  onRun: (action: AgentAction) => Promise<void>;
}): JSX.Element {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void run(action, () => onRun(action))}
    >
      {busy === action ? (
        <Loader2Icon className="animate-spin" aria-hidden />
      ) : (
        icon
      )}
      {label}
    </Button>
  );
}
